import React, { useState, useEffect } from 'react';
import { collectionService } from '../../services/collectionService';
import { filesService } from '../../services/files';
import './CollectionSetBoard.css';

const CollectionSetBoard = ({ channel, isOwner }) => {
    const [sets, setSets] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingSet, setEditingSet] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [imgUrl, setImgUrl] = useState('');
    const [tags, setTags] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    
    // Поиск
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    // Загрузка картинки
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const result = await filesService.uploadFile(file, 'collections');
            setImgUrl(result.url);
        } catch (error) {
            console.error('❌ Ошибка загрузки файла:', error);
        } finally {
            setIsUploading(false);
        }
    };

    // Загрузка наборов
    useEffect(() => {
        if (channel?.id) {
            collectionService.getSets(channel.id)
                .then(data => setSets(data))
                .catch(err => console.error('Ошибка загрузки наборов:', err));
        }
    }, [channel?.id]);

    const handleSave = () => {
        const setData = {
            title,
            description,
            imgUrl,
            tags: tags.split(',').map(t => t.trim()).filter(Boolean),
            siteId: channel.siteId
        };

        if (editingSet) {
            collectionService.updateSet(editingSet.id, setData)
                .then(updated => {
                    setSets(sets.map(s => s.id === editingSet.id ? { ...s, ...updated } : s));
                    closeModal();
                });
        } else {
            collectionService.createSet(channel.id, setData)
                .then(created => {
                    setSets([...sets, created]);
                    closeModal();
                });
        }
    };

    const handleDelete = (id) => {
        if (!window.confirm('Удалить этот набор?')) return;
        collectionService.deleteSet(id)
            .then(() => setSets(sets.filter(s => s.id !== id)));
    };

    const openCreateModal = () => {
        setEditingSet(null);
        setTitle('');
        setDescription('');
        setImgUrl('');
        setTags('');
        setShowModal(true);
    };

    const openEditModal = (set) => {
        setEditingSet(set);
        setTitle(set.title || '');
        setDescription(set.description || '');
        setImgUrl(set.imgUrl || '');
        setTags(set.tags?.join(', ') || '');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingSet(null);
        setTitle('');
        setDescription('');
        setImgUrl('');
        setTags('');
    };

    // Фильтрация по поиску
    const filteredSets = sets.filter(s => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase().trim();
        return (
            s.title?.toLowerCase().includes(q) ||
            s.description?.toLowerCase().includes(q) ||
            s.tags?.some(tag => tag.toLowerCase().includes(q))
        );
    });

    return (
        <div className="collection-board">
            {/* Заголовок */}
            <div className="collection-header">
                <div className="collection-title-group">
                    <h2>📦 {channel.name}</h2>
                    <span className="collection-count-badge">
                        {searchQuery.trim()
                            ? `${filteredSets.length} / ${sets.length}`
                            : sets.length}
                    </span>
                </div>

                <div className="collection-header-actions">
                    {!isSearchOpen ? (
                        <button
                            className="search-icon-btn"
                            onClick={() => setIsSearchOpen(true)}
                            title="Поиск по наборам"
                        >
                            🔍
                        </button>
                    ) : (
                        <div className="collection-search-bar">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="🔍 Поиск..."
                                className="collection-search-input"
                                autoFocus
                            />
                            {searchQuery && (
                                <button
                                    className="search-clear-btn"
                                    onClick={() => setSearchQuery('')}
                                >
                                    ✕
                                </button>
                            )}
                            <button
                                className="search-close-btn"
                                onClick={() => {
                                    setIsSearchOpen(false);
                                    setSearchQuery('');
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    {isOwner && (
                        <button className="add-btn" onClick={openCreateModal}>
                            + Добавить
                        </button>
                    )}
                </div>
            </div>

            {/* Сетка */}
            <div className="collection-grid">
                {filteredSets.length === 0 ? (
                    <div className="empty-collection-msg">
                        {sets.length === 0
                            ? 'В наборе пока нет элементов'
                            : 'Ничего не найдено по запросу'}
                    </div>
                ) : (
                    filteredSets.map(set => (
                        <div key={set.id} className="collection-card">
                            <div className="collection-card-img">
                                {set.imgUrl
                                    ? <img src={set.imgUrl} alt={set.title} />
                                    : <div className="placeholder">📦</div>}

                                {/* Кнопки поверх картинки */}
                                {isOwner && (
                                    <div className="collection-card-actions">
                                        <button
                                            className="card-action-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openEditModal(set);
                                            }}
                                            title="Редактировать"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="card-action-btn delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(set.id);
                                            }}
                                            title="Удалить"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="collection-card-body">
                                <h3>{set.title}</h3>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Модальное окно */}
            {showModal && isOwner && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>{editingSet ? 'Редактировать набор' : 'Новый набор'}</h3>

                        <div className="form-group">
                            <label>Название</label>
                            <input
                                type="text"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                placeholder="Введите название..."
                                className="modal-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Описание</label>
                            <textarea
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Введите описание..."
                                className="modal-textarea"
                            />
                        </div>

                        <div className="form-group">
                            <label>Изображение</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileUpload}
                                className="modal-file-input"
                                disabled={isUploading}
                            />
                            {isUploading && <div className="upload-status">⏳ Загрузка...</div>}
                            {imgUrl && (
                                <div className="icon-preview">
                                    <img src={imgUrl} alt="Превью" className="icon-preview-img" />
                                    <button
                                        className="remove-image-btn"
                                        onClick={() => setImgUrl('')}
                                        type="button"
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label>Теги (через запятую)</label>
                            <input
                                type="text"
                                value={tags}
                                onChange={e => setTags(e.target.value)}
                                placeholder="стратегия, карточная, ..."
                                className="modal-input"
                            />
                        </div>

                        <div className="modal-actions">
                            <button onClick={closeModal}>Отмена</button>
                            <button
                                onClick={handleSave}
                                disabled={!title.trim()}
                            >
                                Сохранить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollectionSetBoard;